import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Smartphone, Search, FileText } from 'lucide-react';
import api from '../../services/api';

const GATEWAY_LABELS: Record<string, string> = { podpay: 'PodPay v1', slimpay: 'Slimpay' };

export const BotsList: React.FC = () => {
    const [bots, setBots] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    const filteredBots = bots.filter(b => {
        if (!searchTerm.trim()) return true;
        const q = searchTerm.toLowerCase();
        return (b.name || '').toLowerCase().includes(q) ||
            (b.owner_phone || '').toLowerCase().includes(q) ||
            (b.owner_name || '').toLowerCase().includes(q) ||
            (b.slug || '').toLowerCase().includes(q);
    });

    useEffect(() => {
        loadBots();
    }, []);

    const loadBots = async () => {
        try {
            setLoading(true);
            const res = await api.get('/banana/bots');
            setBots(res.data.bots);
        } catch (error) {
            console.error('Erro ao carregar bots:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                    <Bot className="text-orange-500" /> Monitoramento de Bots
                </h1>
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="relative w-full sm:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                        <input
                            type="text"
                            placeholder="Buscar por nome, dono ou slug..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-xl py-2 pl-10 pr-4 text-white text-sm focus:border-orange-500/50 outline-none"
                        />
                    </div>
                    <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl px-4 py-2">
                        <span className="text-xs text-orange-500 font-bold uppercase tracking-wider">Total: {filteredBots.length}{searchTerm ? ` de ${bots.length}` : ''} operando</span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {loading ? (
                    <div className="col-span-full p-20 text-center text-gray-500">Carregando frota de bots...</div>
                ) : filteredBots.length === 0 ? (
                    <div className="col-span-full p-20 text-center text-gray-500">
                        {bots.length === 0 ? 'Nenhum bot ativo no momento.' : `Nenhum bot encontrado para "${searchTerm}".`}
                    </div>
                ) : filteredBots.map(bot => (
                    <div key={bot.id} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/[0.07] transition-all">
                        <div className="flex justify-between items-start mb-4">
                            <div className="w-10 h-10 rounded-lg bg-orange-600/20 text-orange-500 flex items-center justify-center">
                                <Bot size={20} />
                            </div>
                            <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${['online', 'active', 'running'].includes(bot.status)
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-red-500/20 text-red-400'
                                }`}>
                                {bot.status?.toUpperCase() || 'OFFLINE'}
                            </span>
                        </div>

                        <h3 className="text-white font-bold mb-1">{bot.name || 'Bot sem nome'}</h3>
                        <div className="flex flex-col gap-1 mb-4">
                            <div className="flex items-center gap-2 text-gray-500 text-xs">
                                <Smartphone size={12} className="text-orange-500" />
                                <span className="text-gray-400">Dono: <strong className="text-white">{bot.owner_name || '—'}</strong></span>
                            </div>
                            {bot.owner_phone && (
                                <div className="flex items-center gap-2 text-gray-500 text-xs pl-5">
                                    <span className="text-[10px] text-gray-600 font-mono">{bot.owner_phone}</span>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/5">
                            <div className="flex flex-col">
                                <span className="text-[10px] text-gray-500 uppercase">Slug</span>
                                <span className="text-xs text-white font-mono truncate">{bot.slug}</span>
                            </div>
                            <div className="flex flex-col items-end">
                                <span className="text-[10px] text-gray-500 uppercase">Adquirente</span>
                                <span className="text-xs text-orange-500 font-bold uppercase">{GATEWAY_LABELS[bot.effective_gateway] || 'PodPay v1'}</span>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={() => navigate(`/banana/bots/${bot.id}`)}
                            className="w-full mt-6 py-2 bg-white/5 hover:bg-white/10 rounded-xl text-xs font-bold text-gray-400 hover:text-white transition-all flex items-center justify-center gap-2"
                        >
                            <FileText size={14} /> Ver detalhes
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};
